import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Pencil, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { Cell, Value, pretty } from "@/components/rcc/Cells";
import { SequenceEditor } from "@/components/rcc/SequenceEditor";
import { InboxThread } from "@/components/rcc/InboxThread";
import { MpcShareCard, ProposalPdfButton } from "@/components/rcc/ShareTools";
import { WorkflowRebuild } from "@/components/rcc/WorkflowRebuild";
import { MpcRadarMatches } from "@/components/radar/SuiteLinks";
import { MpcCandidate } from "@/components/rcc/MpcCandidate";
import { QuickCampaignButton } from "@/components/outreach/QuickCampaign";

const SENDS = ["launch", "send"];

export const DetailSheet = ({ R, name, row, onClose, onEdit, onChanged }) => {
  const [busy, setBusy] = useState("");
  const url = `${R.endpoint}/${row.id}`;
  const act = async (a, label) => {
    if (SENDS.includes(a) && !window.confirm(`${label}: this sends real messages to approved recipients. Continue?`)) return;
    setBusy(a);
    try {
      const { data } = await api.post(`${url}/${a}`, {});
      const rep = data?.launch_report || data?.item?.launch_report;
      if (rep) { const skipped = rep.skipped ?? ((rep.suppressed || 0) + (rep.missing_email || 0)); toast.success(`${label} done: ${rep.queued ?? rep.sent ?? rep.recipients ?? 0} queued${skipped ? `, ${skipped} skipped (suppressed ${rep.suppressed || 0}, no email ${rep.missing_email || 0})` : ""}`); }
      else toast.success(data?.message || `${label} done`);
      onChanged(data?.item || data?.id ? data.item || data : null);
    }
    catch (e) {
      const m = errMsg(e);
      toast.error(e.response?.status === 404 ? `${label} isn't live on the backend yet (spec §31).` : /postal/i.test(m) ? "Launch needs your company postal address (CAN-SPAM). Set COMPANY_POSTAL_ADDRESS to 2800 S Franklin St, Denver, CO 80210 on the backend." : m);
      if (!e.response && a === "run") recheck();
    } finally { setBusy(""); }
  };
  // No reply from the server: re-read the workflow after 20 s in case the run still went through.
  const recheck = () => setTimeout(() => api.get(url).then(({ data }) => {
    const it = data?.item || data;
    if (it?.last_run_at) toast.message(`${row.name || "Workflow"}: last run ${new Date(it.last_run_at).toLocaleString()}`, { description: String(it.last_result || "No result recorded") });
    onChanged(it?.id ? it : null);
  }).catch(() => {}), 20000);
  const del = async () => { if (!window.confirm(`Delete this ${R.noun}?`)) return; try { await api.delete(url); toast.success("Deleted"); onClose(); onChanged(null); } catch (e) { toast.error(errMsg(e)); } };
  const keys = [...R.columns.map((c) => c[0]), ...(R.fields || []).map((f) => f[0]), ...(R.detail || [])].filter((k, i, a) => a.indexOf(k) === i);
  const kind = Object.fromEntries(R.columns.map((c) => [c[0], c[2]]));
  const title = row.name || row.full_name || row.headline || row.title || row.subject || row.provider || row.email || pretty(R.noun);
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto border-[#8224e3]/30 bg-[#0d0a12] sm:max-w-xl" data-testid={`rcc-detail-${name}`}>
        <SheetHeader><SheetTitle className="pr-8 text-left font-display text-xl text-zinc-50">{title}</SheetTitle></SheetHeader>
        <div className="mt-4 flex flex-wrap gap-2">
          {(R.actions || []).map(([a, l]) => <Button key={a} size="sm" variant="outline" disabled={!!busy} onClick={() => act(a, l)} className="border-[#8224e3]/50 text-[#d7b6ff] hover:bg-[#8224e3]/15" data-testid={`rcc-action-${a}`}>{busy === a && <Loader2 className="h-3.5 w-3.5 animate-spin" />}{l}</Button>)}
          {onEdit && <Button size="sm" variant="ghost" onClick={onEdit} data-testid="rcc-edit"><Pencil className="h-3.5 w-3.5" />Edit</Button>}
          {!R.readOnly && <Button size="sm" variant="ghost" onClick={del} className="text-red-400" data-testid="rcc-delete"><Trash2 className="h-3.5 w-3.5" /></Button>}
        </div>
        {name === "mpcs" && <div className="mt-5"><MpcShareCard row={row} /></div>}
        {name === "mpcs" && row.id && <MpcCandidate row={row} />}
        {name === "mpcs" && row.id && <MpcRadarMatches row={row} />}
        {name === "workflows" && row.id && <div className="mt-4 rounded-xl border border-purple-500/30 bg-purple-500/[0.06] p-3" data-testid="wf-campaign-box"><p className="mb-2 text-xs text-zinc-300">Reach the best companies this workflow found with one email campaign (top scorers' decision makers).</p><QuickCampaignButton source="workflow" ids={[row.id]} label={`${row.name} — top companies`} testid="wf-campaign-top">Email campaign on top companies</QuickCampaignButton></div>}
        {name === "proposals" && <div className="mt-4"><ProposalPdfButton row={row} /></div>}
        {name === "workflows" && <WorkflowRebuild row={row} onDone={() => { onClose(); onChanged(null); }} />}
        {R.sequence && <div className="mt-5"><SequenceEditor campaign={row} url={url} onSaved={onChanged} /></div>}
        {R.inbox && <div className="mt-5"><InboxThread row={row} url={url} /></div>}
        <dl className="mt-5 space-y-3">
          {keys.filter((k) => row[k] != null && row[k] !== "").map((k) => (
            <div key={k}><dt className="mb-0.5 text-[10px] uppercase tracking-wider text-zinc-500">{pretty(k)}</dt><dd>{kind[k] ? <Cell v={row[k]} kind={kind[k]} /> : <Value v={row[k]} />}</dd></div>
          ))}
        </dl>
      </SheetContent>
    </Sheet>
  );
};
